import { copy as sharedUiCopy } from '@shared/copy';
import { Skeleton } from '@shared/ui/Skeleton';

/**
 * The pending state of `/editorial/:postId/edit`.
 *
 * The editor's own two-column layout, at its own template: the canvas on the
 * `minmax(0,1fr)` track with the title control and the block flow under it, and
 * the rail on its `20rem` track beside it from the wide tier. The rail comes
 * SECOND in the tree exactly as it does in `PostEditor`, so the phone reading
 * order is the placeholder's reading order too.
 *
 * The canvas is drawn as BLOCKS rather than as one tall panel. A post body is a
 * run of blocks, each with its own handle row, and the single filled rectangle
 * this file used to draw was the tallest thing on the surface and the least
 * informative — it reserved the space and said nothing about what would stand
 * in it.
 *
 * The sticky action row is drawn too: it renders with the editor rather than
 * after it, so leaving it out would push four buttons into view under the
 * operator's eye.
 */
function BlockSkeleton() {
  return (
    <div className="grid gap-2 rounded-md border border-border bg-surface p-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-[5rem] rounded-sm" />
        <Skeleton className="ms-auto h-6 w-6 rounded-sm" />
        <Skeleton className="h-6 w-6 rounded-sm" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

function RailSectionSkeleton({ control }: { readonly control: string }) {
  return (
    <div className="grid gap-2">
      <Skeleton className="h-4 w-[7rem]" />
      <Skeleton className={control} />
    </div>
  );
}

export function EditorialEditSkeleton() {
  return (
    <>
      <p role="status" className="sr-only">
        {sharedUiCopy.state.loading}
      </p>

      <div aria-hidden="true" className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
        <div className="min-w-0">
          {/* The title control is an `h2`-sized input, not a field bar. */}
          <Skeleton className="mb-5 h-[3.25rem] w-full rounded-sm" />

          <div className="grid gap-3">
            <BlockSkeleton />
            <BlockSkeleton />
            <BlockSkeleton />
          </div>
        </div>

        <div className="grid gap-5 rounded-md border border-border bg-surface p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-6 w-[6rem] rounded-pill" />
            <Skeleton className="h-3 w-[6rem]" />
          </div>

          <RailSectionSkeleton control="h-[2.75rem] w-full rounded-sm" />
          <RailSectionSkeleton control="h-[2.75rem] w-full rounded-sm" />
          <RailSectionSkeleton control="h-[6rem] w-full rounded-sm" />
        </div>
      </div>

      <div
        aria-hidden="true"
        className="mt-6 grid gap-3 border-t border-border bg-bg py-4 sm:flex sm:flex-wrap"
      >
        <Skeleton className="h-[2.75rem] w-full rounded-sm sm:w-[7rem]" />
        <Skeleton className="h-[2.75rem] w-full rounded-sm sm:w-[9rem]" />
        <Skeleton className="h-[2.75rem] w-full rounded-sm sm:ms-auto sm:w-[7rem]" />
      </div>
    </>
  );
}
